import { Icon } from '@iconify/react';
import { useClipboard } from '@nextui-org/use-clipboard';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  RootState,
  SETTING_TYPE,
  updateSelectedMessage,
  useAppDispatch,
} from '@circuitry-ai/doc-data';
import { SpeechUtility } from '@circuitry-ai/doc-ui';

interface ChatMessageHoverActionsProps {
  readonly text: string;
  readonly showRead?: boolean;
  readonly isAdvisorView?: boolean;
  readonly className?: string;
  /** Horizontal row below the bubble (design prototype). */
  readonly variant?: 'side' | 'below';
  readonly timestamp?: string;
}

export function ChatMessageHoverActions({
  text,
  showRead = true,
  isAdvisorView = true,
  className = '',
  variant = 'side',
  timestamp,
}: ChatMessageHoverActionsProps) {
  const dispatch = useAppDispatch();
  const { copied, copy } = useClipboard();
  const [isReading, setIsReading] = useState(false);
  const { advisorAbilities } = useSelector((state: RootState) => state.advisor);
  const isSpeakActive = advisorAbilities?.[SETTING_TYPE.SPEAK]?.is_active ?? false;
  const canRead = showRead && isAdvisorView && isSpeakActive;

  const handleCopy = () => {
    copy(text);
    dispatch(updateSelectedMessage(text));
  };

  const handleRead = async () => {
    if (isReading) {
      void (await SpeechUtility.getInstance(dispatch)).handleMute(isReading, setIsReading);
      return;
    }
    void (await SpeechUtility.getInstance(dispatch)).textToSpeech(text, setIsReading);
  };

  if (!text.trim()) return null;

  if (variant === 'below') {
    return (
      <div
        className={`flex items-center gap-0.5 mt-1 self-stretch ${className}`}
      >
        <button
          type="button"
          title="Copy"
          className="w-[26px] h-[26px] border-0 bg-transparent text-default-400 rounded-md inline-flex items-center justify-center cursor-pointer hover:bg-default-100 hover:text-foreground"
          onClick={handleCopy}
        >
          {copied ? (
            <Icon icon="lucide:check" width={14} height={14} />
          ) : (
            <Icon icon="lucide:copy" width={14} height={14} />
          )}
        </button>
        {canRead ? (
          <button
            type="button"
            title="Read aloud"
            className="w-[26px] h-[26px] border-0 bg-transparent text-default-400 rounded-md inline-flex items-center justify-center cursor-pointer hover:bg-default-100 hover:text-foreground"
            onClick={() => void handleRead()}
          >
            <Icon icon="lucide:volume-2" width={14} height={14} />
          </button>
        ) : null}
        {timestamp ? (
          <span className="ml-auto text-[11px] text-default-400 px-1">{timestamp}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center gap-0.5 shrink-0 ${className}`}
    >
      {canRead ? (
        <button
          type="button"
          title="Read aloud"
          className="w-7 h-7 border-0 bg-transparent text-default-400 rounded-full inline-flex items-center justify-center cursor-pointer hover:bg-default-100 hover:text-foreground"
          onClick={() => void handleRead()}
        >
          <Icon icon="lucide:volume-2" width={18} height={18} />
        </button>
      ) : null}
      <button
        type="button"
        title="Copy"
        className="w-7 h-7 border-0 bg-transparent text-default-400 rounded-full inline-flex items-center justify-center cursor-pointer hover:bg-default-100 hover:text-foreground"
        onClick={handleCopy}
      >
        {copied ? (
          <Icon icon="lucide:check" width={18} height={18} />
        ) : (
          <Icon icon="lucide:copy" width={18} height={18} />
        )}
      </button>
    </div>
  );
}
